import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import type { Database } from '@/integrations/supabase/types';
import type { TimeEntryAnalyticsResult } from '@/services/timeEntries/types';
import type { PayslipLineItemInput } from './types';
import {
  computeServiceChargeAllocations,
  type ServiceChargeEmployeeInput,
  type ServiceChargeWeightDef,
  type ServiceChargeAllocation,
} from '@/utils/serviceChargeCore';

export type ServiceChargePeriod = Database['public']['Tables']['service_charge_periods']['Row'];
export type ServiceChargeWeight = Database['public']['Tables']['service_charge_weights']['Row'];
export type ServiceChargeEmployeeWeight = Database['public']['Tables']['service_charge_employee_weights']['Row'];

export interface ServiceChargePeriodInput {
  period_label: string;
  period_start: string;
  period_end: string;
  total_pool_amount: number;
  staff_allocation_percent: number;
  loss_breakage_percent: number;
  training_percent: number;
  exclude_days_below_hours?: number | null;
}

export interface ServiceChargeWeightInput {
  id?: string; // present when editing existing weight, absent for new
  period_id: string;
  weight_label: string;
  weight_multiplier: number;
  display_order: number;
}

export interface ServiceChargeEmployeeAssignment {
  profile_id: string;
  weight_id: string;
  notes?: string;
}

export interface ServiceChargeDistributionResult {
  profileId: string;
  employeeName: string;
  workingDays: number;
  weightMultiplier: number;
  weightLabel: string;
  totalWeightedDays: number;
  distributionAmount: number;
  lineItem: PayslipLineItemInput;
}

export interface ServiceChargePeriodWithWeights extends ServiceChargePeriod {
  weights: ServiceChargeWeight[];
}

export interface ActivePeriodData {
  period_id: string;
  period_label: string;
  period_start: string;
  period_end: string;
  total_pool_amount: number;
  staff_allocation_percent: number;
  staff_allocation_amount: number;
  loss_breakage_amount: number;
  training_amount: number;
  exclude_days_below_hours: number | null;
  weights: Array<{
    id: string;
    weight_label: string;
    weight_multiplier: number;
    display_order: number;
  }>;
}

/**
 * Service Charge Distribution Service
 * Handles monthly service charge pool configuration and automated distribution
 */
export const serviceChargeService = {
  /**
   * Get active service charge period for date range
   */
  async getActivePeriod(startDate: Date, endDate: Date): Promise<ActivePeriodData | null> {
    try {
      const { data, error } = await supabase.rpc('get_active_service_charge_period', {
        p_start_date: format(startDate, 'yyyy-MM-dd'),
        p_end_date: format(endDate, 'yyyy-MM-dd'),
      });

      if (error) {
        console.error('[serviceChargeService] Failed to get active period:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return null;
      }

      return data[0] as ActivePeriodData;
    } catch (error) {
      console.error('[serviceChargeService] Error in getActivePeriod:', error);
      throw error;
    }
  },

  /**
   * Get all service charge periods for the company
   */
  async getPeriods(): Promise<ServiceChargePeriod[]> {
    try {
      const { data, error } = await supabase
        .from('service_charge_periods')
        .select('*')
        .order('period_start', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[serviceChargeService] Error fetching periods:', error);
      throw error;
    }
  },

  /**
   * Get a single period with its weights
   */
  async getPeriodWithWeights(periodId: string): Promise<ServiceChargePeriodWithWeights | null> {
    try {
      const { data: period, error: periodError } = await supabase
        .from('service_charge_periods')
        .select('*')
        .eq('id', periodId)
        .single();

      if (periodError) throw periodError;
      if (!period) return null;

      const { data: weights, error: weightsError } = await supabase
        .from('service_charge_weights')
        .select('*')
        .eq('period_id', periodId)
        .order('display_order');

      if (weightsError) throw weightsError;

      return {
        ...period,
        weights: weights || [],
      };
    } catch (error) {
      console.error('[serviceChargeService] Error fetching period with weights:', error);
      throw error;
    }
  },

  /**
   * Create a new service charge period
   */
  async createPeriod(input: ServiceChargePeriodInput, weights: ServiceChargeWeightInput[]): Promise<ServiceChargePeriod> {
    try {
      // Get current user's profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Create period
      const { data: period, error: periodError } = await supabase
        .from('service_charge_periods')
        .insert({
          ...input,
          company_id: profile.company_id,
          created_by: profile.id,
        })
        .select()
        .single();

      if (periodError) throw periodError;

      // Create weights
      if (weights.length > 0) {
        const weightsToInsert = weights.map(w => ({
          ...w,
          period_id: period.id,
          company_id: profile.company_id,
        }));

        const { error: weightsError } = await supabase
          .from('service_charge_weights')
          .insert(weightsToInsert);

        if (weightsError) throw weightsError;
      }

      return period;
    } catch (error) {
      console.error('[serviceChargeService] Error creating period:', error);
      throw error;
    }
  },

  /**
   * Update a service charge period
   */
  async updatePeriod(periodId: string, updates: Partial<ServiceChargePeriodInput>): Promise<ServiceChargePeriod> {
    try {
      const { data, error } = await supabase
        .from('service_charge_periods')
        .update(updates)
        .eq('id', periodId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[serviceChargeService] Error updating period:', error);
    throw error;
    }
  },

  /**
   * Update weight categories for a period (diff-based: insert new, update existing, delete removed).
   * Returns metadata about cascaded employee assignment deletions.
   */
  async updatePeriodWeights(
    periodId: string,
    desiredWeights: ServiceChargeWeightInput[]
  ): Promise<{ deletedAssignmentCount: number }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Fetch existing weights from DB
      const { data: existingWeights, error: fetchError } = await supabase
        .from('service_charge_weights')
        .select('id')
        .eq('period_id', periodId);

      if (fetchError) throw fetchError;

      const existingIds = new Set((existingWeights || []).map(w => w.id));
      const desiredIds = new Set(desiredWeights.filter(w => w.id).map(w => w.id!));

      // IDs to delete: exist in DB but not in desired list
      const idsToDelete = [...existingIds].filter(id => !desiredIds.has(id));

      // Count employee assignments that will be cascade-deleted
      let deletedAssignmentCount = 0;
      if (idsToDelete.length > 0) {
        const { count, error: countError } = await supabase
          .from('service_charge_employee_weights')
          .select('*', { count: 'exact', head: true })
          .eq('period_id', periodId)
          .in('weight_id', idsToDelete);

        if (countError) throw countError;
        deletedAssignmentCount = count || 0;

        // Delete removed weights (cascade deletes employee assignments)
        const { error: deleteError } = await supabase
          .from('service_charge_weights')
          .delete()
          .in('id', idsToDelete);

        if (deleteError) throw deleteError;
      }

      // Update existing weights
      const toUpdate = desiredWeights.filter(w => w.id && existingIds.has(w.id));
      for (const w of toUpdate) {
        const { error: updateError } = await supabase
          .from('service_charge_weights')
          .update({
            weight_label: w.weight_label,
            weight_multiplier: w.weight_multiplier,
            display_order: w.display_order,
          })
          .eq('id', w.id!);

        if (updateError) throw updateError;
      }

      // Insert new weights
      const toInsert = desiredWeights.filter(w => !w.id);
      if (toInsert.length > 0) {
        const rows = toInsert.map(w => ({
          period_id: periodId,
          company_id: profile.company_id,
          weight_label: w.weight_label,
          weight_multiplier: w.weight_multiplier,
          display_order: w.display_order,
        }));

        const { error: insertError } = await supabase
          .from('service_charge_weights')
          .insert(rows);

        if (insertError) throw insertError;
      }

      return { deletedAssignmentCount };
    } catch (error) {
      console.error('[serviceChargeService] Error updating period weights:', error);
      throw error;
    }
  },

  /**
   * Delete a service charge period
   */
  async deletePeriod(periodId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('service_charge_periods')
        .delete()
        .eq('id', periodId);

      if (error) throw error;
    } catch (error) {
      console.error('[serviceChargeService] Error deleting period:', error);
      throw error;
    }
  },

  /**
   * Get employee weight assignments for a period
   */
  async getEmployeeAssignments(periodId: string): Promise<ServiceChargeEmployeeWeight[]> {
    try {
      const { data, error } = await supabase
        .from('service_charge_employee_weights')
        .select('*')
        .eq('period_id', periodId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[serviceChargeService] Error fetching employee assignments:', error);
      throw error;
    }
  },

  /**
   * Assign weights to employees for a period
   */
  async assignEmployeeWeights(
    periodId: string,
    assignments: ServiceChargeEmployeeAssignment[]
  ): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Delete existing assignments for this period and profiles
      const profileIds = assignments.map(a => a.profile_id);
      const { error: deleteError } = await supabase
        .from('service_charge_employee_weights')
        .delete()
        .eq('period_id', periodId)
        .in('profile_id', profileIds);

      if (deleteError) throw deleteError;

      // Insert new assignments
      const assignmentsToInsert = assignments.map(a => ({
        ...a,
        period_id: periodId,
        company_id: profile.company_id,
        assigned_by: profile.id,
      }));

      const { error: insertError } = await supabase
        .from('service_charge_employee_weights')
        .insert(assignmentsToInsert);

      if (insertError) throw insertError;
    } catch (error) {
      console.error('[serviceChargeService] Error assigning employee weights:', error);
      throw error;
    }
  },

  /**
   * Calculate service charge distribution for employees
   */
  async calculateDistribution(
    period: ActivePeriodData,
    employeeAnalytics: Map<string, { analytics: TimeEntryAnalyticsResult; name: string }>
  ): Promise<Map<string, ServiceChargeDistributionResult>> {
    try {
      const results = new Map<string, ServiceChargeDistributionResult>();

      // Get employee weight assignments for this period
      const { data: assignments, error: assignmentsError } = await supabase
        .from('service_charge_employee_weights')
        .select('profile_id, weight_id')
        .eq('period_id', period.period_id);

      if (assignmentsError) throw assignmentsError;

      // Build assignment map
      const assignmentMap = new Map<string, string>();
      assignments?.forEach(a => {
        assignmentMap.set(a.profile_id, a.weight_id);
      });

      // Map analytics into core allocator input
      const employees: ServiceChargeEmployeeInput[] = [];
      for (const [profileId, { analytics, name }] of employeeAnalytics) {
        employees.push({
          profileId,
          name,
          analytics: {
            dayAnalytics: analytics.dayAnalytics,
            totalWorkedDays: analytics.totalWorkedDays,
          },
        });
      }

      const weights: ServiceChargeWeightDef[] = period.weights.map(w => ({
        id: w.id,
        weight_label: w.weight_label,
        weight_multiplier: w.weight_multiplier,
      }));

      // Delegate to pure core allocator
      const { allocations, totalWeightedDays } = computeServiceChargeAllocations({
        employees,
        assignmentMap,
        weights,
        staffAllocationAmount: period.staff_allocation_amount,
        excludeDaysBelowHours: period.exclude_days_below_hours,
      });

      // Wrap allocations into ServiceChargeDistributionResult with line items
      for (const alloc of allocations) {
        const rateApplied = alloc.workingDays > 0
          ? Math.round((alloc.distributionAmount / alloc.workingDays) * 100) / 100
          : 0;

        const lineItem: PayslipLineItemInput = {
          line_item_type: 'allowance',
          calculation_method: 'fixed_amount',
          description: `Service Charge – ${period.period_label}`,
          category: 'earnings',
          hours_applied: alloc.workingDays,
          rate_applied: rateApplied,
          base_amount: alloc.distributionAmount,
          gross_amount: alloc.distributionAmount,
          net_amount: alloc.distributionAmount,
          is_taxable: false,
          compensation_rule_id: null,
          calculation_notes: `${alloc.workingDays} days × ${alloc.weightMultiplier} (${alloc.weightLabel})`,
          display_order: 100,
        };

        results.set(alloc.profileId, {
          profileId: alloc.profileId,
          employeeName: alloc.name,
          workingDays: alloc.workingDays,
          weightMultiplier: alloc.weightMultiplier,
          weightLabel: alloc.weightLabel,
          totalWeightedDays,
          distributionAmount: alloc.distributionAmount,
          lineItem,
        });
      }

      return results;
    } catch (error) {
      console.error('[serviceChargeService] Error calculating distribution:', error);
      throw error;
    }
  },

  /**
   * Copy weights from a previous period
   */
  async copyWeightsFromPeriod(sourcePeriodId: string, targetPeriodId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Get source assignments
      const { data: sourceAssignments, error: fetchError } = await supabase
        .from('service_charge_employee_weights')
        .select('profile_id, weight_id, notes')
        .eq('period_id', sourcePeriodId);

      if (fetchError) throw fetchError;
      if (!sourceAssignments || sourceAssignments.length === 0) return;

      // Get target period weights to map labels
      const { data: sourceWeights } = await supabase
        .from('service_charge_weights')
        .select('id, weight_label')
        .eq('period_id', sourcePeriodId);

      const { data: targetWeights } = await supabase
        .from('service_charge_weights')
        .select('id, weight_label')
        .eq('period_id', targetPeriodId);

      // Create label to ID map for target period
      const targetWeightMap = new Map<string, string>();
      targetWeights?.forEach(w => {
        targetWeightMap.set(w.weight_label, w.id);
      });

      const sourceWeightMap = new Map<string, string>();
      sourceWeights?.forEach(w => {
        sourceWeightMap.set(w.id, w.weight_label);
      });

      // Map assignments to target period
      const newAssignments = sourceAssignments
        .map(a => {
          const label = sourceWeightMap.get(a.weight_id);
          if (!label) return null;
          const targetWeightId = targetWeightMap.get(label);
          if (!targetWeightId) return null;

          return {
            period_id: targetPeriodId,
            profile_id: a.profile_id,
            weight_id: targetWeightId,
            notes: a.notes,
            company_id: profile.company_id,
            assigned_by: profile.id,
          };
        })
        .filter((a): a is NonNullable<typeof a> => a !== null);

      if (newAssignments.length === 0) return;

      // Insert new assignments
      const { error: insertError } = await supabase
        .from('service_charge_employee_weights')
        .insert(newAssignments);

      if (insertError) throw insertError;
    } catch (error) {
      console.error('[serviceChargeService] Error copying weights:', error);
      throw error;
    }
  },

  /**
   * Preview distribution for a period (without creating payslip items)
   */
  async previewDistribution(periodId: string, startDate: Date, endDate: Date) {
    try {
      // Get period with weights
      const { data: period, error: periodError } = await supabase
        .from('service_charge_periods')
        .select('*')
        .eq('id', periodId)
        .single();

      if (periodError) throw periodError;
      if (!period) throw new Error('Period not found');

      // Get weights
      const { data: weightRows, error: weightsError } = await supabase
        .from('service_charge_weights')
        .select('*')
        .eq('period_id', periodId)
        .order('display_order');

      if (weightsError) throw weightsError;

      // Get employee assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('service_charge_employee_weights')
        .select(`
          profile_id,
          weight_id,
          profiles:profile_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('period_id', periodId);

      if (assignmentsError) throw assignmentsError;

      if (!assignments || assignments.length === 0) {
        return {
          period,
          employees: [],
          summary: {
            totalWorkingDays: 0,
            totalWeightedDays: 0,
            totalDistributed: 0,
            employeesAssigned: 0,
            staffAllocationAmount: Number(period.staff_allocation_amount),
          },
          warnings: ['No employees assigned to this period'],
        };
      }

      // Fetch time entry analytics for all assigned employees
      const { timeEntryAnalytics } = await import('@/services/timeEntries');
      const profileIds = assignments.map(a => a.profile_id);
      const analyticsMap = await timeEntryAnalytics.analyzeMultipleEmployees(
        profileIds,
        startDate,
        endDate
      );

      // Build assignment map and employee inputs for core allocator
      const assignmentMap = new Map<string, string>();
      const nameMap = new Map<string, string>();
      assignments.forEach(a => {
        assignmentMap.set(a.profile_id, a.weight_id);
        const profile = (a as any).profiles;
        const firstName = profile?.first_name || '';
        const lastName = profile?.last_name || '';
        nameMap.set(a.profile_id, `${firstName} ${lastName}`.trim() || 'Unknown');
      });

      const employees: ServiceChargeEmployeeInput[] = [];
      for (const [profileId, analytics] of analyticsMap) {
        const name = nameMap.get(profileId) || 'Unknown';
        employees.push({
          profileId,
          name,
          analytics: {
            dayAnalytics: analytics.dayAnalytics,
            totalWorkedDays: analytics.totalWorkedDays,
          },
        });
      }

      const weights: ServiceChargeWeightDef[] = (weightRows || []).map(w => ({
        id: w.id,
        weight_label: w.weight_label,
        weight_multiplier: w.weight_multiplier,
      }));

      const staffAllocationAmount = Number(period.staff_allocation_amount);

      // Delegate to pure core allocator
      const { allocations, totalWeightedDays } = computeServiceChargeAllocations({
        employees,
        assignmentMap,
        weights,
        staffAllocationAmount,
        excludeDaysBelowHours: period.exclude_days_below_hours
          ? Number(period.exclude_days_below_hours)
          : null,
      });

      // Format for UI
      const employeeData = allocations.map(alloc => ({
        profileId: alloc.profileId,
        name: alloc.name,
        workingDays: alloc.workingDays,
        weightLabel: alloc.weightLabel,
        weightMultiplier: alloc.weightMultiplier,
        weightedDays: alloc.weightedDays,
        distributionAmount: alloc.distributionAmount,
        percentOfPool: staffAllocationAmount > 0
          ? (alloc.distributionAmount / staffAllocationAmount) * 100
          : 0,
      }));

      // Summary
      const totalWorkingDays = employeeData.reduce((sum, emp) => sum + emp.workingDays, 0);
      const totalDistributed = employeeData.reduce((sum, emp) => sum + emp.distributionAmount, 0);

      // Warnings
      const warnings: string[] = [];
      const employeesWithZeroDays = employeeData.filter(emp => emp.workingDays === 0);
      if (employeesWithZeroDays.length > 0) {
        warnings.push(`${employeesWithZeroDays.length} employee(s) have 0 working days in this period`);
      }
      if (period.exclude_days_below_hours && totalWeightedDays === 0 && employeeData.length > 0) {
        warnings.push(
          `All employees have 0 eligible days after excluding days with ≤${period.exclude_days_below_hours} hours. ` +
          `Consider lowering the threshold or removing it.`
        );
      }

      return {
        period,
        employees: employeeData,
        summary: {
          totalWorkingDays,
          totalWeightedDays,
          totalDistributed,
          employeesAssigned: employeeData.length,
          staffAllocationAmount,
        },
        warnings,
      };
    } catch (error) {
      console.error('[serviceChargeService] Error previewing distribution:', error);
      throw error;
    }
  },
};
